import type { ModelRegistry } from 'ember-data/types/registries/model';
import { getInitials } from 'uplisting-frontend/utils/get-initials';
import type { PersonKind } from 'uplisting-frontend/utils/teammate-people';
import {
  shapeKeyFor,
  TEAMMATE_SHAPES,
  type TeammateShape,
  type TeammateShapeKey,
} from 'uplisting-frontend/utils/teammate-shapes';

export type PersonRecordKind = Exclude<PersonKind, 'owner'>;
export type PersonRecord =
  | ModelRegistry['teammate']
  | ModelRegistry['invitation'];

export interface ParsedPersonId {
  kind: PersonRecordKind;
  id: string;
}

interface TeammatePersonOptions {
  presetsEnabled: boolean;
  clientManagementEnabled: boolean;
}

const PERSON_RECORD_KINDS: readonly string[] = ['teammate', 'invitation'];

export function personIdFor(kind: PersonRecordKind, id: string): string {
  return `${kind}-${id}`;
}

export function looseUnitsFor(
  record: PersonRecord,
): ModelRegistry['multi-unit'][] {
  const { properties, multiUnits } = record;

  return multiUnits.filter(
    (multiUnit) => !properties.includes(multiUnit.property),
  );
}

export function hasAllPropertyAccess(
  propertyCount: number,
  unitCount: number,
  totalProperties: number,
): boolean {
  return totalProperties > 0 && propertyCount === totalProperties && !unitCount;
}

export function parsePersonId(value: string): ParsedPersonId | undefined {
  const separator = value.indexOf('-');

  if (separator === -1) {
    return undefined;
  }

  const kind = value.slice(0, separator);
  const id = value.slice(separator + 1);

  if (!id || !PERSON_RECORD_KINDS.includes(kind)) {
    return undefined;
  }

  return { kind: kind as PersonRecordKind, id };
}

export class TeammatePerson {
  readonly kind: PersonRecordKind;
  readonly record: PersonRecord;

  private readonly presetsEnabled: boolean;
  private readonly clientManagementEnabled: boolean;

  constructor(
    kind: PersonRecordKind,
    record: PersonRecord,
    options: TeammatePersonOptions,
  ) {
    this.kind = kind;
    this.record = record;
    this.presetsEnabled = options.presetsEnabled;
    this.clientManagementEnabled = options.clientManagementEnabled;
  }

  get id(): string {
    return personIdFor(this.kind, this.record.id);
  }

  get isPending(): boolean {
    return this.kind === 'invitation';
  }

  get teammate(): ModelRegistry['teammate'] | undefined {
    return this.kind === 'teammate'
      ? (this.record as ModelRegistry['teammate'])
      : undefined;
  }

  get invitation(): ModelRegistry['invitation'] | undefined {
    return this.kind === 'invitation'
      ? (this.record as ModelRegistry['invitation'])
      : undefined;
  }

  get name(): string {
    const { teammate, invitation } = this;

    if (teammate) {
      return teammate.name;
    }

    return invitation?.fullName || invitation?.email || '';
  }

  get initials(): string {
    return getInitials(this.name);
  }

  get email(): string {
    return this.record.email;
  }

  get phoneNumber(): string | undefined {
    return this.record.phoneNumber;
  }

  get roleKey(): TeammateShapeKey {
    return shapeKeyFor(this.record, this.presetsEnabled);
  }

  get shape(): TeammateShape | undefined {
    return TEAMMATE_SHAPES.find((item) => item.key === this.roleKey);
  }

  get showMultiUnits(): boolean {
    return this.shape?.showMultiUnits ?? true;
  }

  get createdAt(): Date {
    return this.record.createdAt;
  }

  get propertyCount(): number {
    return this.record.properties.length;
  }

  get looseUnits(): ModelRegistry['multi-unit'][] {
    return looseUnitsFor(this.record);
  }

  get unitCount(): number {
    return this.looseUnits.length;
  }

  get isClientManaged(): boolean {
    return (
      (this.shape?.disableClientManaged ?? false) &&
      this.clientManagementEnabled &&
      this.record.hasClient
    );
  }

  get canEditProperties(): boolean {
    return this.kind === 'teammate' && !this.isClientManaged;
  }
}
